import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEnterToAdvance } from './useEnterToAdvance';
import { GettingStartedChecklistItem, GsStepId } from './useGettingStartedOnboarding';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';

/**
 * GettingStarted-scoped onboarding stepper.
 *
 * Classic one-step-at-a-time wizard: only the current (first unfinished) step
 * is rendered. A row of dots below the card serves as the progress indicator —
 * each dot fills in as its step is completed or skipped. Skip / unskip is
 * delegated to the parent via `onSkip`, wired to `useGettingStartedOnboarding`.
 */

interface OnboardingChecklistProps {
  checklist: GettingStartedChecklistItem[];
  onSkip: (stepId: GsStepId, skip: boolean) => void;
  onComplete: (stepId: GsStepId, complete: boolean) => void;
  isSkipPending?: boolean;
}

export const OnboardingChecklist = ({
  checklist,
  onSkip,
  onComplete,
  isSkipPending = false,
}: OnboardingChecklistProps) => {
  const { t } = useTranslation('getting_started');

  // First step that is neither completed nor skipped — the only step the user
  // sees. When every step is finished the parent (`OnboardingProgress`) has
  // already unmounted us, so the fallback to `null` is defensive.
  const currentIndex = useMemo(
    () => checklist.findIndex((item) => !item.completed && !item.skipped),
    [checklist],
  );
  const currentItem = currentIndex >= 0 ? checklist[currentIndex] : null;

  // Track which step's primary action is currently in-flight. Prevents the
  // user from double-firing async Continue/Finish handlers (validation +
  // KB upload + importUrl) — observed in production as rage-clicks on
  // `Next` and `Finish` while the click handler awaited a mutation. The
  // step id (rather than a boolean) keeps the disabled state pinned to a
  // specific step, so a fast user clicking Continue → step advances →
  // clicking the next Continue still works without an extra render.
  const [pendingActionStepId, setPendingActionStepId] = useState<GsStepId | null>(null);

  const handleSkip = useCallback(
    (e: React.MouseEvent, stepId: GsStepId) => {
      e.stopPropagation();
      onSkip(stepId, true);
    },
    [onSkip],
  );

  const advanceStep = useCallback(
    async (item: GettingStartedChecklistItem) => {
      // Steps that own async work (e.g. firing a mutation on Continue) pass an
      // `onPrimaryAction` and are responsible for marking themselves complete.
      if (item.onPrimaryAction) {
        if (pendingActionStepId === item.id) return;
        setPendingActionStepId(item.id);
        try {
          await item.onPrimaryAction();
        } finally {
          setPendingActionStepId((prev) => (prev === item.id ? null : prev));
        }
        return;
      }
      onComplete(item.id, true);
    },
    [onComplete, pendingActionStepId],
  );

  const handleComplete = useCallback(
    (e: React.MouseEvent, item: GettingStartedChecklistItem) => {
      e.stopPropagation();
      void advanceStep(item);
    },
    [advanceStep],
  );

  const enterHandler = useMemo(
    () => (currentItem ? () => advanceStep(currentItem) : null),
    [currentItem, advanceStep],
  );
  useEnterToAdvance(enterHandler);

  // "Back" un-completes the previous step so the "first unfinished" selector
  // lands back on it. Steps that own a different bail-out target (e.g. the
  // first step returning to the URL-first entry screen) supply `onBackAction`
  // to override the default.
  const handleBack = useCallback(
    (e: React.MouseEvent, item: GettingStartedChecklistItem) => {
      e.stopPropagation();
      if (item.onBackAction) {
        item.onBackAction();
        return;
      }
      const idx = checklist.findIndex((i) => i.id === item.id);
      if (idx <= 0) return;
      const prevId = checklist[idx - 1].id;
      onComplete(prevId, false);
    },
    [checklist, onComplete],
  );

  if (!currentItem) return null;

  return (
    <div data-testid="gs-onboarding-checklist">
      <div
        className="flex flex-col gap-6 bg-card border border-border rounded-xl p-6 min-h-[500px]"
        data-testid={`gs-checklist-item-${currentItem.id}`}
      >
        <div>
          <p
            className="text-xl font-semibold text-foreground"
            data-testid={`gs-checklist-item-title-${currentItem.id}`}
          >
            {currentItem.title}
          </p>
          {currentItem.description && (
            <p
              className="text-sm font-medium text-muted-foreground mt-1"
              data-testid={`gs-checklist-item-subtitle-${currentItem.id}`}
            >
              {currentItem.description}
            </p>
          )}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentItem.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="flex flex-1 flex-col"
          >
            {currentItem.expandedContent && (
              <div data-testid={`gs-checklist-item-content-${currentItem.id}`}>
                {currentItem.expandedContent}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Rendered outside `AnimatePresence` so the buttons stay put as the
            step content fades in/out — the user clicks Continue and the same
            button sits in the same pixel position on the next step. */}
        <div className="flex items-center justify-end gap-3">
          {currentItem.showBackAction && (
            <Button
              size="sm"
              variant="link"
              onClick={(e) => handleBack(e, currentItem)}
              // Pinned during an async primary action (e.g. the KB step's DoH
              // check): navigating back mid-await leaves the pending Continue to
              // import and complete a step the user just left.
              disabled={pendingActionStepId === currentItem.id}
              className="!text-foreground/60 hover:!opacity-100 hover:!text-foreground"
              data-testid={`gs-checklist-item-back-button-${currentItem.id}`}
            >
              <ChevronLeft />
              {t('back')}
            </Button>
          )}
          {currentItem.primaryActionLabel ? (
            <Button
              size="sm"
              onClick={(e) => handleComplete(e, currentItem)}
              disabled={pendingActionStepId === currentItem.id}
              className="min-w-16"
              data-testid={`gs-checklist-item-continue-button-${currentItem.id}`}
            >
              {pendingActionStepId === currentItem.id ? (
                <Spinner className="size-4" />
              ) : (
                <>
                  {currentItem.primaryActionLabel}
                  <ChevronRight />
                </>
              )}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={(e) => handleSkip(e, currentItem.id)}
              disabled={isSkipPending}
              data-testid={`gs-checklist-item-skip-button-${currentItem.id}`}
            >
              {t('skip_step')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
